import {
  MAPPABLE_FIELD_LABELS,
  type InboundEventDto,
  type MappingPreview,
} from '@performance-crm/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/toast-context';
import { useEditInboundEvent, useImport, usePreviewMapping } from '@/lib/queries';

/**
 * Corrige en pantalla una fila de importación que falló (celular mal escrito, sin datos de contacto…):
 * muestra las columnas del archivo, comprueba el resultado con el mismo mapeo de la carga y reprocesa solo esa fila.
 */
export function FixRowDialog({
  event,
  companyId,
  onClose,
}: {
  event: InboundEventDto;
  companyId: string;
  onClose: () => void;
}) {
  const { data: imp } = useImport(event.importId ?? undefined);
  const preview = usePreviewMapping();
  const edit = useEditInboundEvent();
  const toast = useToast();
  const original = useMemo(
    () =>
      Object.fromEntries(
        Object.entries((event.payload as Record<string, unknown>) ?? {}).map(([k, v]) => [
          k,
          v === null || v === undefined ? '' : String(v),
        ]),
      ),
    [event.payload],
  );
  const [values, setValues] = useState<Record<string, string>>(original);
  const [result, setResult] = useState<MappingPreview | null>(null);
  // Columnas en el orden del archivo (las importaciones antiguas sin cabeceras guardadas usan las del payload).
  const columns = useMemo(() => {
    const fromImport = imp?.headers ?? [];
    const extra = Object.keys(original).filter((k) => !fromImport.includes(k));
    return [...fromImport, ...extra];
  }, [imp, original]);
  /** Campo del CRM al que va cada columna, para mostrarlo junto a la cabecera. */
  const fieldOf = (header: string) => {
    const entry = Object.entries(imp?.fieldMap ?? {}).find(([, h]) => h === header);
    return entry ? MAPPABLE_FIELD_LABELS[entry[0] as keyof typeof MAPPABLE_FIELD_LABELS] : null;
  };

  // Vista previa con el mismo mapeo, valores por defecto, proyecto y asesor de la carga (con retardo al escribir).
  const previewKey = JSON.stringify(values);
  useEffect(() => {
    if (!imp) return;
    let cancelled = false;
    const t = setTimeout(() => {
      preview
        .mutateAsync({
          companyId,
          fieldMap: imp.fieldMap,
          defaults: imp.defaults,
          projectId: imp.projectId,
          advisorId: imp.advisorId,
          payload: values,
        })
        .then((r) => {
          if (!cancelled) setResult(r);
        })
        .catch(() => undefined);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imp?.id, previewKey, companyId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await edit.mutateAsync({ id: event.id, payload: values });
      toast.success('Fila corregida', 'Se vuelve a procesar; en unos segundos verás el resultado.');
      onClose();
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  const changed = JSON.stringify(values) !== JSON.stringify(original);
  const ready = !!result && result.issues.length === 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Corregir fila {event.externalId ?? ''}</DialogTitle>
            <DialogDescription>
              Edita los datos tal como vinieron en el archivo. Al guardar, solo esta fila se vuelve
              a procesar con el mismo mapeo de la carga.
            </DialogDescription>
          </DialogHeader>
          {event.error && (
            <p className="text-danger rounded-md border px-3 py-2 text-sm" data-testid="fix-error">
              {event.error}
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {columns.map((h) => (
              <div key={h} className="space-y-1">
                <Label htmlFor={`fix-${h}`} className="text-xs">
                  {h}
                  {fieldOf(h) && (
                    <span className="text-muted-foreground font-normal"> → {fieldOf(h)}</span>
                  )}
                </Label>
                <Input
                  id={`fix-${h}`}
                  value={values[h] ?? ''}
                  onChange={(e) => setValues({ ...values, [h]: e.target.value })}
                  data-testid={`fix-${h}`}
                />
              </div>
            ))}
          </div>
          <div className="rounded-md border p-3 text-sm" data-testid="fix-preview">
            {!result || preview.isPending ? (
              <p className="text-muted-foreground flex items-center gap-2 text-xs">
                <Loader2 className="h-3 w-3 animate-spin" /> Comprobando…
              </p>
            ) : (
              <>
                <p className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {[result.mapped.firstName, result.mapped.lastNamePaternal]
                      .filter(Boolean)
                      .join(' ') || 'Sin nombre'}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {result.mapped.phone ?? result.mapped.email ?? '—'} ·{' '}
                    {result.projectName ?? '—'} ·{' '}
                    {result.identity.activeLeadInProject && result.identity.activeLeadAdvisorName
                      ? result.identity.activeLeadAdvisorName
                      : (result.advisorName ?? 'Por turnos')}
                  </span>
                  {ready &&
                    (!result.identity.contactId ? (
                      <Badge tone="success">nuevo</Badge>
                    ) : result.identity.activeLeadInProject ? (
                      <Badge tone="warning">recontacto · se anota en su lead</Badge>
                    ) : (
                      <Badge tone="warning">recontacto · lead nuevo</Badge>
                    ))}
                  {ready && result.identity.doNotContact && (
                    <Badge tone="danger" title="Pidió que no le escribamos: sin WhatsApp ni bot">
                      no contactar
                    </Badge>
                  )}
                </p>
                {result.issues.length > 0 && (
                  <ul className="text-danger mt-2 space-y-0.5 text-xs">
                    {result.issues.map((i) => (
                      <li key={i}>⚠ {i}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={edit.isPending || !changed || !ready}
              title={
                !changed
                  ? 'Cambia algún dato para poder guardar'
                  : !ready
                    ? 'Corrige los problemas señalados'
                    : undefined
              }
              data-testid="fix-save"
            >
              Guardar y reprocesar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
